/**
 * Where the portal's events go — the screen that replaced an environment variable.
 *
 * Two halves, and the second is the one that earns its keep:
 *
 *   Destinations — somewhere events are sent, with the two filters that decide which ones.
 *   Delivery log — every attempt, INCLUDING the ones a filter skipped. An event that
 *                  arrived nowhere and left no trace is the failure this page exists to
 *                  make impossible, so a skip is shown as loudly as a failure.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Webhook, Plus, Send, Trash2, Pencil, RefreshCw, CheckCircle2, XCircle, SkipForward,
  History,
} from 'lucide-react';

const ALL = '*';

const STATUS_STYLES = {
  delivered: 'bg-green-100 text-green-700 border-green-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
  skipped: 'bg-gray-100 text-gray-500 border-gray-200',
};

const STATUS_ICON = {
  delivered: CheckCircle2,
  failed: XCircle,
  skipped: SkipForward,
};

const blankForm = {
  name: '', url: '', secret: '', events: ALL, stages: [], active: true,
};

const STAGE_EVENT = 'partner_stage_changed';
// Kept in step with OPT_IN_ONLY in backend/webhook_destinations.py — "everything" means
// every domain event, and display text is not one.
const OPT_IN_ONLY = ['notification'];

/** Both filters read back as one sentence, so the two cannot be quietly conflated. */
const summarise = (form) => {
  const all = form.events === ALL;
  const list = all ? [] : form.events;
  const getsStageEvent = all || list.includes(STAGE_EVENT);
  const stagesNarrowed = getsStageEvent && form.stages.length > 0;

  const others = all
    ? `every event the portal emits except ${OPT_IN_ONLY.join(', ')}`
    : (list.filter((e) => e !== STAGE_EVENT).join(', ') || null);

  const parts = [];
  if (others) parts.push(others);
  if (getsStageEvent) {
    parts.push(stagesNarrowed
      ? `stage changes, but only to: ${form.stages.join(', ')}`
      : 'stage changes, at every stage');
  }
  if (!parts.length) return 'Nothing — no events are ticked.';
  if (all && stagesNarrowed) {
    // The one combination that surprises people: "everything" plus a stage filter.
    return `Every event the portal emits except ${OPT_IN_ONLY.join(', ')} — and of stage `
      + `changes, only: ${form.stages.join(', ')}.`;
  }
  return `${parts.join('; and ')}.`;
};

const AdminWebhooks = () => {
  const { getAuthHeaders, API_URL } = useAuth();
  const [destinations, setDestinations] = useState([]);
  const [eventCatalog, setEventCatalog] = useState([]);
  const [stageCatalog, setStageCatalog] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // null = dialog closed
  const [form, setForm] = useState(blankForm);
  const [logFilter, setLogFilter] = useState({ destination_id: '', status: '' });
  const [viewing, setViewing] = useState(null);   // the delivery whose payload is open
  const [backfilling, setBackfilling] = useState('');

  const auth = useCallback(() => ({ headers: getAuthHeaders() }), [getAuthHeaders]);

  const fetchDestinations = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/webhooks`, auth());
      setDestinations(data.destinations || []);
      setEventCatalog(data.event_catalog || []);
      setStageCatalog(data.stage_catalog || []);
    } catch (e) {
      toast.error('Could not load webhook destinations');
    } finally {
      setLoading(false);
    }
  }, [API_URL, auth]);

  const fetchDeliveries = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (logFilter.destination_id) params.set('destination_id', logFilter.destination_id);
      if (logFilter.status) params.set('status', logFilter.status);
      const { data } = await axios.get(
        `${API_URL}/api/admin/webhooks/deliveries?${params.toString()}`, auth());
      setDeliveries(data.deliveries || []);
    } catch (e) {
      toast.error('Could not load the delivery log');
    }
  }, [API_URL, auth, logFilter]);

  useEffect(() => { fetchDestinations(); }, [fetchDestinations]);
  useEffect(() => { fetchDeliveries(); }, [fetchDeliveries]);

  const openNew = () => { setForm(blankForm); setEditing('new'); };

  const openEdit = (dest) => {
    setForm({
      name: dest.name || '',
      url: dest.url || '',
      // Never prefilled — the API does not return it. Left blank means "keep the one
      // that is stored"; typing replaces it.
      secret: '',
      events: dest.events || ALL,
      stages: dest.stages || [],
      active: dest.active !== false,
    });
    setEditing(dest.id);
  };

  const save = async () => {
    if (!form.url.trim()) { toast.error('A destination needs a URL'); return; }
    try {
      if (editing === 'new') {
        await axios.post(`${API_URL}/api/admin/webhooks`, form, auth());
        toast.success('Destination added');
      } else {
        await axios.put(`${API_URL}/api/admin/webhooks/${editing}`, form, auth());
        toast.success('Destination saved');
      }
      setEditing(null);
      fetchDestinations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the destination');
    }
  };

  const remove = async (dest) => {
    if (!window.confirm(`Delete "${dest.name}"? Events will stop going there.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/webhooks/${dest.id}`, auth());
      toast.success('Destination deleted');
      fetchDestinations();
    } catch (e) {
      toast.error('Could not delete the destination');
    }
  };

  const test = async (dest) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/webhooks/${dest.id}/test`, {}, auth());
      if (data.ok) toast.success(`${dest.name} answered ${data.http_status}`);
      else toast.error(`${dest.name}: ${data.http_status || 'no response'} — ${data.response}`);
      fetchDestinations();
      fetchDeliveries();
    } catch (e) {
      toast.error('Could not send the test');
    }
  };

  const backfill = async (dest) => {
    const ok = window.confirm(
      `Send every partner's current stage to "${dest.name}"?\n\n`
      + 'Only this destination is told — the others are not re-sent anything they already '
      + 'know. Use this after connecting a new CRM org so quiet partners get a deal too.');
    if (!ok) return;
    setBackfilling(dest.id);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/webhooks/${dest.id}/backfill`, {}, auth());
      toast.success(
        `${data.sent} of ${data.partners} partners sent to ${dest.name}`
        + (data.errors ? ` · ${data.errors} errored` : ''));
      fetchDestinations();
      fetchDeliveries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backfill failed');
    } finally {
      setBackfilling('');
    }
  };

  const resend = async (row) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/webhooks/deliveries/${row.id}/resend`, {}, auth());
      if (data.ok) toast.success(`Resent to ${data.destination}`);
      else toast.error(`${data.destination} refused it again`);
      fetchDeliveries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not resend');
    }
  };

  const subscribesToAll = form.events === ALL;
  const eventList = subscribesToAll ? [] : form.events;

  const toggleEvent = (name) => {
    const next = eventList.includes(name)
      ? eventList.filter((e) => e !== name)
      : [...eventList, name];
    setForm({ ...form, events: next.length ? next : ALL });
  };

  const toggleStage = (key) => {
    const next = form.stages.includes(key)
      ? form.stages.filter((s) => s !== key)
      : [...form.stages, key];
    setForm({ ...form, stages: next });
  };

  const stageFilterApplies = subscribesToAll || eventList.includes('partner_stage_changed');

  const describeFilter = (dest) => {
    const events = dest.events === ALL ? 'All events' : `${dest.events.length} events`;
    if (!dest.stages?.length) return events;
    return `${events} · ${dest.stages.length} stage${dest.stages.length > 1 ? 's' : ''}`;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-webhooks">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A1A]">Outgoing Webhooks</h1>
            <p className="text-sm text-[#64748B] mt-1">
              Where partner events are sent. Add a destination for each CRM org that should
              hear from the portal, and choose which events it gets.
            </p>
          </div>
          <Button onClick={openNew} className="bg-[#ef785b] hover:bg-[#e5674a] text-white h-9">
            <Plus className="w-4 h-4 mr-1" /> Add destination
          </Button>
        </div>

        {/* Destinations */}
        <Card className="bg-white border-[#E2E2DF]">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ef785b]" />
              </div>
            ) : destinations.length === 0 ? (
              <div className="text-center py-12 text-[#64748B]">
                <Webhook className="w-12 h-12 mx-auto mb-3 text-[#E2E2DF]" />
                <p>No destinations yet — events are going nowhere.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E2DF]">
                    <TableHead className="text-xs font-semibold text-[#64748B]">Name</TableHead>
                    <TableHead className="text-xs font-semibold text-[#64748B]">URL</TableHead>
                    <TableHead className="text-xs font-semibold text-[#64748B]">Sends</TableHead>
                    <TableHead className="text-xs font-semibold text-[#64748B]">Secret</TableHead>
                    <TableHead className="text-xs font-semibold text-[#64748B]">Last delivery</TableHead>
                    <TableHead className="text-xs font-semibold text-[#64748B]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {destinations.map((dest) => (
                    <TableRow key={dest.id} className="border-[#E2E2DF]">
                      <TableCell>
                        <div className="font-medium text-[#1A1A1A]">{dest.name}</div>
                        {!dest.active && (
                          <span className="text-xs text-[#64748B]">switched off</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs font-mono text-[#64748B] max-w-[280px] truncate">
                        {dest.url}
                      </TableCell>
                      <TableCell className="text-sm text-[#64748B]">
                        {describeFilter(dest)}
                      </TableCell>
                      <TableCell className="text-xs font-mono text-[#64748B]">
                        {dest.has_secret ? dest.secret_hint : '—'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {dest.last_status ? (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_STYLES[dest.last_status]}`}>
                            {dest.last_status}
                          </span>
                        ) : <span className="text-[#64748B]">never</span>}
                        <div className="text-xs text-[#64748B] mt-0.5">
                          {dest.delivered_count || 0} ok · {dest.failed_count || 0} failed
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" className="h-8 px-2"
                                  onClick={() => test(dest)} title="Send a test ping">
                            <Send className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 px-2"
                                  disabled={backfilling === dest.id}
                                  onClick={() => backfill(dest)}
                                  title="Send every partner's current stage to this destination">
                            <History className={`w-4 h-4 ${backfilling === dest.id ? 'animate-spin' : ''}`} />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 px-2"
                                  onClick={() => openEdit(dest)} title="Edit">
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm"
                                  className="h-8 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => remove(dest)} title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Delivery log */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-lg font-bold text-[#1A1A1A]">Delivery log</h2>
              <p className="text-sm text-[#64748B]">
                Every attempt, including the ones a filter skipped — so "it never arrived"
                always has an answer.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Select value={logFilter.destination_id || 'all'}
                      onValueChange={(v) => setLogFilter({ ...logFilter, destination_id: v === 'all' ? '' : v })}>
                <SelectTrigger className="w-[200px] h-9 border-[#E2E2DF]">
                  <SelectValue placeholder="All destinations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All destinations</SelectItem>
                  {destinations.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={logFilter.status || 'all'}
                      onValueChange={(v) => setLogFilter({ ...logFilter, status: v === 'all' ? '' : v })}>
                <SelectTrigger className="w-[150px] h-9 border-[#E2E2DF]">
                  <SelectValue placeholder="All outcomes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All outcomes</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="skipped">Skipped</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-9 border-[#E2E2DF]"
                      onClick={fetchDeliveries}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-0">
              {deliveries.length === 0 ? (
                <div className="text-center py-10 text-[#64748B]">
                  <p>Nothing logged yet.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#E2E2DF]">
                      <TableHead className="text-xs font-semibold text-[#64748B]">When</TableHead>
                      <TableHead className="text-xs font-semibold text-[#64748B]">Event</TableHead>
                      <TableHead className="text-xs font-semibold text-[#64748B]">Destination</TableHead>
                      <TableHead className="text-xs font-semibold text-[#64748B]">Outcome</TableHead>
                      <TableHead className="text-xs font-semibold text-[#64748B]">Detail</TableHead>
                      <TableHead className="text-xs font-semibold text-[#64748B]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deliveries.map((row) => {
                      const Icon = STATUS_ICON[row.status] || SkipForward;
                      return (
                        <TableRow key={row.id} className="border-[#E2E2DF]">
                          <TableCell className="text-xs text-[#64748B] whitespace-nowrap">
                            {new Date(row.created_at).toLocaleString('en-IN', {
                              day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                            })}
                          </TableCell>
                          <TableCell className="text-sm text-[#1A1A1A]">
                            <div className="font-medium">{row.event_type}</div>
                            {row.to_stage && (
                              <div className="text-xs text-[#64748B]">{row.to_stage}</div>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-[#64748B]">
                            {row.destination_name}
                          </TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_STYLES[row.status]}`}>
                              <Icon className="w-3 h-3" />
                              {row.status}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs text-[#64748B] max-w-[320px] truncate"
                                     title={row.reason || row.response}>
                            {row.reason || row.response || '—'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 justify-end">
                              <Button variant="ghost" size="sm" className="h-8 px-2 text-xs"
                                      onClick={() => setViewing(row)}>
                                Payload
                              </Button>
                              {/* A skip was a decision, not a failure — replaying it would
                                  post the very event the filter was configured to hold back. */}
                              {row.status === 'failed' && (
                                <Button variant="ghost" size="sm" className="h-8 px-2 text-xs"
                                        onClick={() => resend(row)}>
                                  Resend
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* What we actually posted. Stored per attempt, so this is the body as it was sent
          at the time — not one rebuilt from the partner's record as it reads today. */}
      <Dialog open={viewing !== null} onOpenChange={(open) => !open && setViewing(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>{viewing?.event_type}</span>
              {viewing && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_STYLES[viewing.status]}`}>
                  {viewing.status}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {viewing && (
            <div className="space-y-3 overflow-y-auto">
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                <div><span className="text-[#64748B]">Destination</span> · {viewing.destination_name}</div>
                <div><span className="text-[#64748B]">Sent</span> · {new Date(viewing.created_at).toLocaleString('en-IN')}</div>
                {viewing.http_status != null && (
                  <div><span className="text-[#64748B]">HTTP</span> · {viewing.http_status}</div>
                )}
                {viewing.partner_id && (
                  <div className="font-mono text-xs">
                    <span className="text-[#64748B] font-sans text-sm">Partner</span> · {viewing.partner_id}
                  </div>
                )}
              </div>

              {viewing.reason && (
                <div className="rounded-md bg-[#FAFAF8] border border-[#E2E2DF] p-3 text-sm text-[#1A1A1A]">
                  {viewing.reason}
                </div>
              )}

              {viewing.response && (
                <div>
                  <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wide mb-1">
                    What came back
                  </p>
                  <pre className="text-xs bg-[#1A1A1A] text-[#E2E2DF] rounded-md p-3 overflow-x-auto whitespace-pre-wrap">
                    {viewing.response}
                  </pre>
                </div>
              )}

              <div>
                <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wide mb-1">
                  What we sent
                </p>
                <pre className="text-xs bg-[#1A1A1A] text-[#E2E2DF] rounded-md p-3 overflow-auto max-h-[45vh]">
                  {JSON.stringify(viewing.payload, null, 2)}
                </pre>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" className="h-9 border-[#E2E2DF]"
                    onClick={() => navigator.clipboard?.writeText(JSON.stringify(viewing?.payload, null, 2))
                      .then(() => toast.success('Payload copied'))
                      .catch(() => toast.error('Could not copy'))}>
              Copy JSON
            </Button>
            <Button className="bg-[#ef785b] hover:bg-[#e5674a] text-white h-9"
                    onClick={() => setViewing(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add / edit */}
      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing === 'new' ? 'Add a destination' : 'Edit destination'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="wh-name">Name</Label>
              <Input id="wh-name" value={form.name} className="h-9 mt-1"
                     placeholder="Partnerships CRM"
                     onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>

            <div>
              <Label htmlFor="wh-url">URL</Label>
              <Input id="wh-url" value={form.url} className="h-9 mt-1 font-mono text-xs"
                     placeholder="https://api.expedify.ai/hooks/…/partner-portal-events"
                     onChange={(e) => setForm({ ...form, url: e.target.value })} />
            </div>

            <div>
              <Label htmlFor="wh-secret">Secret</Label>
              <Input id="wh-secret" value={form.secret} className="h-9 mt-1 font-mono text-xs"
                     placeholder={editing === 'new' ? 'whsec_…' : 'leave blank to keep the current secret'}
                     onChange={(e) => setForm({ ...form, secret: e.target.value })} />
              <p className="text-xs text-[#64748B] mt-1">
                Sent as the <code>x-webhook-secret</code> header. In Expedify this is the
                incoming webhook's secret key.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Switch id="wh-active" checked={form.active}
                      onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <Label htmlFor="wh-active">Active</Label>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label>1 · Which events arrive</Label>
                <button type="button"
                        className="text-xs text-[#ef785b] hover:underline"
                        onClick={() => setForm({ ...form, events: subscribesToAll ? [] : ALL })}>
                  {subscribesToAll ? 'Choose specific events' : 'Send everything'}
                </button>
              </div>
              <p className="text-xs text-[#64748B] mt-0.5">
                Each of these is a separate kind of thing that happened — a lead was
                submitted, a commission was earned, a partner moved along the journey.
              </p>
              {subscribesToAll ? (
                <p className="text-sm text-[#64748B] mt-2">
                  This destination receives every event the portal emits, except{' '}
                  <code>notification</code> — that one is bell-icon text written for a
                  person, carries no <code>partner_id</code>, and has to be picked by name.
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-2 mt-2 max-h-52 overflow-y-auto pr-1">
                  {eventCatalog.map((name) => (
                    <label key={name} className="flex items-center gap-2 text-sm text-[#1A1A1A]">
                      <Checkbox checked={eventList.includes(name)}
                                onCheckedChange={() => toggleEvent(name)} />
                      <span className="font-mono text-xs">{name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {stageFilterApplies && (
              <div className="border-l-2 border-[#E2E2DF] pl-3">
                <Label>2 · Which stages, within <code>partner_stage_changed</code> only</Label>
                <p className="text-xs text-[#64748B] mt-0.5">
                  A partner moving along the journey is <strong>one</strong> event type, not
                  twelve — the stage is a field inside it. So this narrows that single event
                  and has no effect on any other. Leave every box empty to get all twelve.
                </p>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {stageCatalog.map((s) => (
                    <label key={s.key} className="flex items-center gap-2 text-sm text-[#1A1A1A]">
                      <Checkbox checked={form.stages.includes(s.key)}
                                onCheckedChange={() => toggleStage(s.key)} />
                      <span>{s.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Both filters, read back as one sentence. The two are easy to conflate —
                one picks event types, the other narrows a single event type — and a
                sentence is a faster check than re-reading which boxes are ticked. */}
            <div className="rounded-md bg-[#FAFAF8] border border-[#E2E2DF] p-3">
              <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wide">
                This destination will receive
              </p>
              <p className="text-sm text-[#1A1A1A] mt-1">{summarise(form)}</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" className="h-9 border-[#E2E2DF]"
                    onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="bg-[#ef785b] hover:bg-[#e5674a] text-white h-9" onClick={save}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default AdminWebhooks;
